import React, { useState } from 'react';
import {
    View, Text, StyleSheet, ScrollView,
} from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStageTokens } from '../hooks/useStageTokens';
import { FriendlyButton } from './FriendlyButton';
import { AudioPromptButton } from './AudioPromptButton';
import { SkiaCanvas } from './SkiaCanvas';
import { useStrokeStore } from '../store/useStrokeStore';

interface InkStepAssessmentActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

interface PseudoWordItem {
    word: string;
    position: number;
}

/**
 * InkStepAssessmentActivity — student-facing pseudo-word reading + writing
 * assessment activity.
 *
 * The child sees a pseudo-word (nonsense word like "bim", "fot"), reads it
 * aloud, and writes it on the canvas. The handwriting is captured for OCR
 * scoring. This is the core assessment component of the InkStep phonics track.
 *
 * WP-068: Entry & Periodic Phonics Assessment.
 *
 * Touch targets are 56px minimum per rules/09-ui-and-styling.md.
 * No bare catch blocks — errors are surfaced with gentle, visible feedback.
 */
export const InkStepAssessmentActivity: React.FC<InkStepAssessmentActivityProps> = ({
    activity,
    onComplete,
    keyStage,
}) => {
    const config = (activity.config || {}) as { pseudoWords?: PseudoWordItem[] };
    const pseudoWords: PseudoWordItem[] = Array.isArray(config.pseudoWords) ? config.pseudoWords : [];
    const tokens = useStageTokens(keyStage);
    const clearStrokes = useStrokeStore((state) => state.clearStrokes);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const currentWord = pseudoWords[currentIndex];
    const totalWords = pseudoWords.length;

    const handleNext = () => {
        try {
            if (currentIndex < totalWords - 1) {
                clearStrokes();
                setCurrentIndex(currentIndex + 1);
                setError(null);
            } else {
                // Assessment complete
                clearStrokes();
                onComplete();
            }
        } catch (err: unknown) {
            // Gentle error for children — never silent
            const message = err instanceof Error ? err.message : 'Let\'s try again!';
            setError(message);
        }
    };

    const handlePrevious = () => {
        try {
            if (currentIndex > 0) {
                clearStrokes();
                setCurrentIndex(currentIndex - 1);
                setError(null);
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Let\'s try again!';
            setError(message);
        }
    };

    if (totalWords === 0) {
        return (
            <View style={styles.container}>
                <Text style={styles.emptyText} maxFontSizeMultiplier={1.5}>
                    No words to practice
                </Text>
            </View>
        );
    }

    return (
        <ScrollView contentContainerStyle={styles.container}>
            {/* Progress indicator */}
            <View style={styles.progressContainer}>
                <Text style={styles.progressText} maxFontSizeMultiplier={1.5}>
                    {currentIndex + 1} of {totalWords}
                </Text>
            </View>

            {/* Pseudo-word display */}
            <View style={styles.wordContainer}>
                <Text
                    style={[styles.pseudoWord, { fontSize: tokens.titleFontSize }]}
                    maxFontSizeMultiplier={1.5}
                    accessibilityLabel={`Word to write: ${currentWord.word}`}
                >
                    {currentWord.word}
                </Text>
                <Text style={styles.positionLabel} maxFontSizeMultiplier={1.5}>
                    Position {currentWord.position}
                </Text>
            </View>

            {/* Audio prompt for the pseudo-word */}
            <View style={styles.audioContainer}>
                <AudioPromptButton
                    text={`Read the word: ${currentWord.word}`}
                    autoPlay={tokens.autoPlayAudio}
                />
            </View>

            {/* Writing area — SkiaCanvas for handwriting capture */}
            <View style={styles.canvasContainer}>
                <View testID="inkstep-canvas" style={styles.canvas}>
                    <SkiaCanvas />
                </View>
            </View>

            {/* Error message — gentle, visible, never silent */}
            {error && (
                <View style={styles.errorContainer}>
                    <Text style={styles.errorText} maxFontSizeMultiplier={1.5}>
                        {error}
                    </Text>
                </View>
            )}

            {/* Navigation buttons — 56px minimum touch targets */}
            <View style={styles.buttonRow}>
                {currentIndex > 0 && (
                    <FriendlyButton
                        testID="inkstep-previous"
                        onPress={handlePrevious}
                        size="large"
                        accessibilityLabel="Previous word"
                        accessibilityHint="Go back to the previous word"
                    >
                        Back
                    </FriendlyButton>
                )}
                <FriendlyButton
                    testID="inkstep-next"
                    onPress={handleNext}
                    size="large"
                    accessibilityLabel={currentIndex < totalWords - 1 ? "Next word" : "Finish assessment"}
                    accessibilityHint={currentIndex < totalWords - 1 ? "Go to the next word" : "Complete the assessment"}
                >
                    {currentIndex < totalWords - 1 ? 'Next' : 'Finish'}
                </FriendlyButton>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        padding: 16,
        backgroundColor: '#F8FAFC',
    },
    progressContainer: {
        alignItems: 'center',
        paddingVertical: 8,
    },
    progressText: {
        fontSize: 18,
        color: '#64748B',
        fontWeight: '600',
    },
    wordContainer: {
        alignItems: 'center',
        paddingVertical: 24,
    },
    pseudoWord: {
        fontWeight: 'bold',
        color: '#1E293B',
        letterSpacing: 2,
    },
    positionLabel: {
        fontSize: 14,
        color: '#94A3B8',
        marginTop: 4,
    },
    audioContainer: {
        alignItems: 'center',
        marginBottom: 16,
    },
    canvasContainer: {
        flex: 1,
        marginBottom: 16,
    },
    canvas: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#E2E8F0',
    },
    errorContainer: {
        backgroundColor: '#FEF3C7',
        borderRadius: 8,
        padding: 12,
        marginBottom: 12,
        alignItems: 'center',
    },
    errorText: {
        fontSize: 16,
        color: '#92400E',
        fontWeight: '500',
    },
    buttonRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        paddingBottom: 8,
    },
    navButton: {
        minHeight: 56,
        minWidth: 56,
        flex: 1,
    },
    emptyText: {
        fontSize: 20,
        color: '#64748B',
        textAlign: 'center',
        marginTop: 40,
    },
});
